import moment from 'moment'
import { useSelector } from 'react-redux'
import useSWR from 'swr'

import type { EligibilityRequest, EligibleDiscount } from '@library/api-promotions'
import { eligibilityFetcher } from '@library/api-promotions'

import { getBasketDate } from 'selectors/basket'

type UseDeliveryDateDiscountArgs = {
  enabled: boolean
  boxType?: 'subscription' | 'transactional'
  // Explicit delivery date to query eligibility for. Defaults to the basket
  // date when omitted (the Menu consumer relies on this). Accepts a date or a
  // date-time string; it is normalised to YYYY-MM-DD.
  deliveryDate?: string
}

type UseDeliveryDateDiscountResult = {
  discount: EligibleDiscount | null
  // True while enabled but the delivery date hasn't hydrated yet; false once resolved or disabled.
  isLoading: boolean
}

type EligibilityKey = EligibilityRequest & { scope: 'promotions-eligibility' }

const fetchEligibility = ({ scope: _scope, ...request }: EligibilityKey) =>
  eligibilityFetcher(request)

export const useDeliveryDateDiscount = ({
  enabled,
  boxType,
  deliveryDate,
}: UseDeliveryDateDiscountArgs): UseDeliveryDateDiscountResult => {
  const basketDate = useSelector(getBasketDate)

  const sourceDate = deliveryDate ?? basketDate
  const parsedDate = sourceDate ? moment(sourceDate) : null
  const deliveryDateFormatted = parsedDate?.isValid() ? parsedDate.format('YYYY-MM-DD') : ''

  const key: EligibilityKey | null =
    enabled && deliveryDateFormatted
      ? {
          scope: 'promotions-eligibility',
          delivery_date: deliveryDateFormatted,
          box_type: boxType,
        }
      : null

  const { data, isLoading: isFetchLoading } = useSWR(key, fetchEligibility, {
    shouldRetryOnError: false,
  })

  // SWR reports isLoading: false for a null key, but enabled with no resolved
  // delivery date means we're still waiting on data, not a final "no discount".
  const isLoading = enabled && !deliveryDateFormatted ? true : isFetchLoading

  return { discount: data?.data?.eligible?.[0] ?? null, isLoading }
}
